
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../app/providers/AuthProvider'
import { API_BASE } from '../lib/api'

const Login = () => {
  const navigate = useNavigate()
  const { login, token, user, loading: authLoading } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')
    setLoading(true)

    try {
      await login(email, password)
      navigate('/app', { replace: true })
    } catch (err) {
      setError(err.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const handleContinue = () => {
    navigate('/app', { replace: true })
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-surface text-ink">
      <div className="absolute inset-0 bg-grid opacity-70" aria-hidden="true" />
      <div className="absolute -top-32 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-glow-cyan" aria-hidden="true" />
      <div className="absolute -bottom-24 right-10 h-72 w-72 rounded-full bg-glow-mint" aria-hidden="true" />

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-6xl items-start justify-center px-6 py-12 md:items-center">
        <div className="glass-panel w-full max-w-md rounded-3xl p-8">
          <div className="mb-6 text-center">
            <img
              src="/logo.jpeg"
              alt="Flynt logo"
              className="mx-auto mb-4 h-14 w-28 bg-surface object-contain logo-zoom"
            />
            <h1 className="text-2xl font-semibold">Welcome back</h1>
            <p className="mt-2 text-sm text-slate-500">Sign in to continue your deployments.</p>
          </div>

          {token && user && !authLoading ? (
            <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-100 p-4 text-sm">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Single click</div>
              <div className="mt-2 font-semibold text-ink">Continue as {user.email}</div>
              <button
                type="button"
                onClick={handleContinue}
                className="mt-3 w-full rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-ink transition hover:bg-white"
              >
                Continue
              </button>
              <div className="mt-3 text-xs text-slate-500">Or sign in with another account below.</div>
            </div>
          ) : null}

          {/* Google Login Button */}
          <a
            href={`${API_BASE}/auth/google`}
            className="mb-4 flex w-full items-center justify-center gap-2 rounded-full border border-slate-300 bg-white/90 px-5 py-3 text-sm font-semibold text-ink transition hover:translate-y-[-1px] hover:bg-white hover:shadow-sm"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Continue with Google
          </a>

          <div className="relative mb-4 flex items-center justify-center">
            <span className="absolute bg-white px-2 text-xs uppercase text-slate-400">or</span>
            <div className="h-px w-full bg-slate-200"></div>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Email</label>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-sm outline-none transition focus:border-slate-400 focus:bg-white"
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Password</label>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-sm outline-none transition focus:border-slate-400 focus:bg-white"
                placeholder="********"
                required
              />
            </div>

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-600">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={loading}
              className="brand-button w-full rounded-full px-5 py-3 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            New here?{' '}
            <Link to="/signup" className="font-semibold text-slate-900">
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
